import { FC, useState } from "react";
import { flushSync } from "react-dom";
import DeleteDialog from "@/components/dialogs/delete-dialog";
import {
  showErrorToast,
  showSuccessToast,
} from "@/components/custom-toasts/toasts";
import useWorkspace from "@/hooks/useWorkspace";
import Workspace, {
  getAllWorkspaceDocuments,
} from "@/models/workspace/Workspace";
import { logger } from "@/lib/logger";
import {
  formatBulkDeleteConfirmationPhrase,
  formatBulkDeletePartialFailureMessage,
  formatBulkDeleteSuccessMessage,
  formatBulkDeleteTotalFailureMessage,
} from "../utils/orphanedWorkspacesSelection";

const MAX_LISTED_WORKSPACE_NAMES = 5;

interface BulkDeleteOrphanedWorkspacesDialogProps {
  isOpen: boolean;
  onClose: () => void;
  workspaces: Workspace[];
  onDeleteSuccess: () => void;
  onPartialDelete: (succeededIds: string[]) => void;
}

const BulkDeleteOrphanedWorkspacesDialog: FC<
  BulkDeleteOrphanedWorkspacesDialogProps
> = ({ isOpen, onClose, workspaces, onDeleteSuccess, onPartialDelete }) => {
  const { deleteWorkspace, invalidateWorkspaceQueries } = useWorkspace();
  const [isDeleting, setIsDeleting] = useState(false);

  if (!isOpen || workspaces.length === 0) return null;

  const workspaceCount = workspaces.length;
  const confirmationPhrase =
    formatBulkDeleteConfirmationPhrase(workspaceCount);
  const documentCount = workspaces.reduce(
    (sum, workspace) => sum + getAllWorkspaceDocuments(workspace).length,
    0
  );
  const listedWorkspaces = workspaces.slice(0, MAX_LISTED_WORKSPACE_NAMES);
  const remainingNameCount = workspaceCount - listedWorkspaces.length;

  const handleDelete = async () => {
    const totalCount = workspaces.length;
    setIsDeleting(true);

    try {
      const results = await Promise.allSettled(
        workspaces.map((workspace) =>
          deleteWorkspace(workspace.id).then(() => workspace.id)
        )
      );

      const succeededIds = results
        .filter(
          (result): result is PromiseFulfilledResult<string> =>
            result.status === "fulfilled"
        )
        .map((result) => result.value);

      const successCount = succeededIds.length;

      const firstFailure = results.find(
        (result): result is PromiseRejectedResult =>
          result.status === "rejected"
      );
      if (firstFailure) {
        logger.error("Failed to delete orphaned workspaces", {
          operation: "bulkDeleteOrphanedWorkspaces",
          error: firstFailure.reason,
          workspaceCount: totalCount,
          failedCount: totalCount - successCount,
        });
      }

      if (successCount > 0) {
        invalidateWorkspaceQueries();
      }

      if (successCount === totalCount) {
        flushSync(() => {
          onDeleteSuccess();
        });
        showSuccessToast(
          formatBulkDeleteSuccessMessage(successCount),
          "Slettet"
        );
        onClose();
      } else if (successCount > 0) {
        flushSync(() => {
          onPartialDelete(succeededIds);
        });
        showErrorToast(
          formatBulkDeletePartialFailureMessage(successCount, totalCount),
          "Delvis sletning"
        );
        onClose();
      } else {
        showErrorToast(formatBulkDeleteTotalFailureMessage(), "Fejl");
      }
    } catch (error) {
      logger.error("Failed to delete orphaned workspaces", {
        operation: "bulkDeleteOrphanedWorkspaces",
        error,
        workspaceCount: totalCount,
      });
      showErrorToast(formatBulkDeleteTotalFailureMessage(), "Fejl");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <DeleteDialog
      isOpen={isOpen}
      setIsOpen={(open) => {
        if (!open) onClose();
      }}
      isDisabled={isDeleting}
      onDelete={handleDelete}
      entityName={confirmationPhrase}
      confirmationPhrase={confirmationPhrase}
      dialogTitle="Slet valgte workspaces"
      confirmButtonText="Slet workspaces"
      isDeleteLoading={isDeleting}
      customTrigger
      extraDescription={
        <div className="space-y-3 text-center text-sm text-muted-foreground">
          <p>
            Du sletter {workspaceCount}{" "}
            {workspaceCount === 1 ? "workspace" : "workspaces"} med i alt{" "}
            {documentCount}{" "}
            {documentCount === 1 ? "dokument" : "dokumenter"}.
          </p>
          <ul className="text-left list-disc list-inside mx-auto max-w-sm">
            {listedWorkspaces.map((workspace) => (
              <li key={workspace.id} className="truncate">
                {workspace.name}
              </li>
            ))}
            {remainingNameCount > 0 && (
              <li className="text-muted-foreground">
                + {remainingNameCount} flere
              </li>
            )}
          </ul>
        </div>
      }
    />
  );
};

export default BulkDeleteOrphanedWorkspacesDialog;
